import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/store';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Loader2, ChevronsUpDown, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

/**
 * TallyImportDialog
 * -----------------
 * Two-step wizard:
 *   1. Upload Tally stock Excel → backend returns AI-matched preview.
 *   2. Review/override each row via a per-row combobox → user confirms → backend applies updates.
 *
 * Each row's combobox shows:
 *   • the row's top fuzzy suggestions (auto-ranked by rapidfuzz)
 *   • below that, the FULL stock-tracked catalog so the owner can pick any
 *     product manually when the fuzzy match is wrong (e.g. dev catalog gaps,
 *     unusual brand spellings).
 *
 * The stock-write step uses Tally's parsed thickness+size, not the catalog
 * product's — so a 7x4 / 81x32 / 6x3 Tally row creates the correct stock
 * entry even if the catalog only carries the 8x4 variant of that brand.
 */
export default function TallyImportDialog({ open, onClose, onApplied }) {
  const fileRef = useRef(null);
  const [phase, setPhase] = useState('upload'); // 'upload' | 'preview' | 'applying'
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filename, setFilename] = useState('');
  const [search, setSearch] = useState('');
  const [catalogOptions, setCatalogOptions] = useState([]);

  // Load full catalog once when dialog opens so each row's combobox has
  // an offline-searchable backing list (~hundreds of products max).
  useEffect(() => {
    if (!open || catalogOptions.length) return;
    api.tallyCatalogOptions()
      .then((res) => setCatalogOptions(res.data || []))
      .catch(() => setCatalogOptions([]));
  }, [open, catalogOptions.length]);

  const reset = () => {
    setPhase('upload');
    setRows([]);
    setSummary(null);
    setFilename('');
    setSearch('');
    setBusy(false);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) {
      toast.error('Please upload an .xlsx file from Tally');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.tallyStockPreview(fd);
      const previewRows = (res.data.rows || []).map((r) => ({
        ...r,
        selectedProductId: r.best_match_id || '',
        // Auto-include learned (previously confirmed) + high/medium rows.
        // Low + none default OFF so the user has to confirm before low-score
        // writes happen. Learned mappings are the strongest signal of all.
        included: r.confidence === 'learned' || r.confidence === 'high' || r.confidence === 'medium',
      }));
      setRows(previewRows);
      setSummary(res.data.summary);
      setFilename(res.data.filename || f.name);
      setPhase('preview');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to parse Excel';
      toast.error(msg);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows.map((r, idx) => ({ ...r, _idx: idx }));
    const q = search.toLowerCase();
    return rows
      .map((r, idx) => ({ ...r, _idx: idx }))
      .filter((r) => r.tally_name?.toLowerCase().includes(q));
  }, [rows, search]);

  const includedCount = useMemo(
    () => rows.filter((r) => r.included && r.selectedProductId).length,
    [rows]
  );

  // Lookup catalog options by both product_id AND by (brand+sub+cat) tuple —
  // because catalogOptions is deduped to one representative product per
  // brand-series group, the specific product_id stored in a learned mapping
  // or fuzzy suggestion often won't match. The tuple lookup is the reliable
  // way to find the aggregated available_thicknesses / available_sizes.
  const catalogById = useMemo(() => {
    const m = new Map();
    catalogOptions.forEach((o) => m.set(o.product_id, o));
    return m;
  }, [catalogOptions]);
  const catalogByGroup = useMemo(() => {
    const m = new Map();
    catalogOptions.forEach((o) => {
      m.set(`${o.brand_name}||${o.subcategory}||${o.main_category}`, o);
    });
    return m;
  }, [catalogOptions]);

  const handleApply = async () => {
    const entries = rows
      .filter((r) => r.included && r.selectedProductId)
      .map((r) => ({
        product_id: r.selectedProductId,
        quantity: Number(r.quantity) || 0,
        tally_name: r.tally_name,
        thickness: r.thickness_parsed || '',
        size: r.size_parsed || '',
      }));
    if (!entries.length) {
      toast.error('Nothing to apply — tick at least one row with a match');
      return;
    }
    setBusy(true);
    setPhase('applying');
    try {
      const res = await api.tallyStockApply({ entries, reason: `Tally import · ${filename}` });
      const { updated = 0, created = 0, skipped = 0 } = res.data || {};
      toast.success(`Stock updated: ${updated} edited, ${created} created${skipped ? `, ${skipped} skipped` : ''}`);
      onApplied?.();
      handleClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to apply stock updates';
      toast.error(msg);
      setPhase('preview');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-6xl max-h-[92vh] flex flex-col" data-testid="tally-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#556B2F]" />
            Import Stock from Tally Excel
          </DialogTitle>
          <DialogDescription>
            Upload the Tally stock export. We&rsquo;ll match each item to your catalog so you can review and approve before any stock value changes.
            For low-confidence rows, click the match field to search the full catalog manually.
          </DialogDescription>
        </DialogHeader>

        {phase === 'upload' && (
          <div className="py-8 flex flex-col items-center justify-center gap-4 border-2 border-dashed border-[#E2E6D9] rounded-lg" data-testid="tally-upload-zone">
            <Upload className="w-12 h-12 text-[#8F9485]" />
            <p className="text-sm text-[#5C6150] text-center max-w-md">
              Choose the <strong>Tally stock register</strong> Excel file. We expect columns like <code>Particulars</code> and <code>Closing Balance</code> &mdash; both standard Tally exports work.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleFile}
              data-testid="tally-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="bg-[#556B2F] hover:bg-[#465926] text-white"
              data-testid="tally-pick-file-btn"
            >
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading...</> : <><Upload className="w-4 h-4 mr-2" /> Choose Tally Excel</>}
            </Button>
          </div>
        )}

        {phase === 'preview' && summary && (
          <div className="flex flex-col flex-1 overflow-hidden">
            <SummaryBar summary={summary} filename={filename} includedCount={includedCount} />

            <div className="my-3 flex items-center gap-2">
              <Input
                placeholder="Filter rows by Tally name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="max-w-xs"
                data-testid="tally-search-input"
              />
              <span className="text-xs text-[#8F9485]">{filteredRows.length} of {rows.length} rows</span>
            </div>

            <div className="flex-1 overflow-auto border border-[#E2E6D9] rounded">
              <table className="w-full text-sm" data-testid="tally-preview-table">
                <thead className="sticky top-0 bg-[#F6F4EC] text-[#5C6150] text-xs uppercase z-10">
                  <tr>
                    <th className="px-2 py-2 text-left w-10">Use</th>
                    <th className="px-2 py-2 text-left w-[35%]">Tally Name</th>
                    <th className="px-2 py-2 text-right w-20">Qty</th>
                    <th className="px-2 py-2 text-left">Matched Product</th>
                    <th className="px-2 py-2 text-center w-24">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <PreviewRow
                      key={r._idx}
                      row={r}
                      catalogOptions={catalogOptions}
                      catalogById={catalogById}
                      catalogByGroup={catalogByGroup}
                      onToggle={(v) => updateRow(r._idx, { included: v })}
                      onPick={(pid) => updateRow(r._idx, { selectedProductId: pid, included: !!pid })}
                      onQtyChange={(q) => updateRow(r._idx, { quantity: q })}
                      onThicknessChange={(t) => updateRow(r._idx, { thickness_parsed: t })}
                      onSizeChange={(s) => updateRow(r._idx, { size_parsed: s })}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {phase === 'applying' && (
          <div className="py-12 flex flex-col items-center gap-3" data-testid="tally-applying">
            <Loader2 className="w-10 h-10 animate-spin text-[#556B2F]" />
            <p className="text-sm text-[#5C6150]">Updating stock... do not close this window.</p>
          </div>
        )}

        <DialogFooter className="flex flex-row items-center justify-between gap-2 mt-3">
          <div className="text-xs text-[#8F9485]">
            {phase === 'preview' && (
              <span>
                Ready to update <strong>{includedCount}</strong> stock {includedCount === 1 ? 'entry' : 'entries'}.
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleClose} disabled={busy} data-testid="tally-cancel-btn">
              Cancel
            </Button>
            {phase === 'preview' && (
              <Button
                onClick={handleApply}
                disabled={busy || !includedCount}
                className="bg-[#556B2F] hover:bg-[#465926] text-white"
                data-testid="tally-apply-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Approve & Update Stock ({includedCount})
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryBar({ summary, filename, includedCount }) {
  // Tier order matters: learned first (strongest signal), then auto/likely/needs/no-match.
  const tiers = [
    { key: 'learned', label: 'Remembered from last import', icon: Sparkles, color: 'bg-indigo-100 text-indigo-700 border-indigo-300' },
    { key: 'high', label: 'Auto-matched', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    { key: 'medium', label: 'Likely match', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700 border-amber-300' },
    { key: 'low', label: 'Needs review', icon: AlertTriangle, color: 'bg-orange-100 text-orange-700 border-orange-300' },
    { key: 'none', label: 'No match', icon: XCircle, color: 'bg-red-100 text-red-700 border-red-300' },
  ];
  return (
    <div className="rounded-lg border border-[#E2E6D9] bg-[#F6F4EC] p-3" data-testid="tally-summary-bar">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-[#5C6150]">
          <span className="font-semibold text-[#1A1C16]">{filename}</span>
          <span className="mx-2">·</span>
          <span>{summary.total_rows} rows parsed</span>
          <span className="mx-2">·</span>
          <span>{includedCount} selected to apply</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {tiers.map((t) => {
            const Icon = t.icon;
            const count = summary[t.key] || 0;
            if (!count && t.key === 'learned') return null; // hide chip when no learned rows
            return (
              <span key={t.key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs ${t.color}`}>
                <Icon className="w-3 h-3" /> {t.label}: {count}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PreviewRow({ row, catalogOptions, catalogById, catalogByGroup, onToggle, onPick, onQtyChange, onThicknessChange, onSizeChange }) {
  const tierStyles = {
    learned: 'bg-indigo-100 text-indigo-700 border-indigo-300',
    high: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    medium: 'bg-amber-100 text-amber-700 border-amber-300',
    low: 'bg-orange-100 text-orange-700 border-orange-300',
    none: 'bg-red-100 text-red-700 border-red-300',
  };
  const tierLabel = {
    learned: 'remembered',
    high: 'high',
    medium: 'medium',
    low: 'low',
    none: 'none',
  };
  const rowBg = row.included ? '' : 'bg-[#FAF8F2] opacity-70';
  // Two-stage resolve: by product_id first (fast path when picker picked
  // directly from catalogOptions), then by (brand+sub+cat) tuple — needed
  // when the row's product_id came from a fuzzy suggestion or a learned
  // mapping referring to a sibling product in the same brand-series group.
  const directHit = row.selectedProductId ? catalogById.get(row.selectedProductId) : null;
  const sugMatch = row.suggestions?.find((s) => s.product_id === row.selectedProductId);
  const groupKey = sugMatch
    ? `${sugMatch.brand_name}||${sugMatch.subcategory}||${sugMatch.main_category}`
    : null;
  const groupHit = groupKey ? catalogByGroup.get(groupKey) : null;
  const selected = directHit || groupHit || null;
  const selectedLabel = selected?.label
    || sugMatch?.label
    || '';

  return (
    <tr className={`border-t border-[#E2E6D9] ${rowBg}`} data-testid={`tally-row-${row._idx}`}>
      <td className="px-2 py-2 align-top">
        <input
          type="checkbox"
          checked={!!row.included}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={!row.selectedProductId}
          data-testid={`tally-row-include-${row._idx}`}
        />
      </td>
      <td className="px-2 py-2 font-mono text-xs align-top">
        <div className="font-medium text-[#1A1C16]">{row.tally_name}</div>
      </td>
      <td className="px-2 py-2 text-right align-top">
        <input
          type="number"
          value={row.quantity ?? 0}
          onChange={(e) => onQtyChange(parseInt(e.target.value, 10) || 0)}
          className="w-16 text-right border border-[#E2E6D9] rounded px-1 py-0.5 text-sm"
          data-testid={`tally-row-qty-${row._idx}`}
        />
      </td>
      <td className="px-2 py-2 align-top">
        <div className="space-y-1.5">
          <MatchCombobox
            rowIdx={row._idx}
            selectedId={row.selectedProductId}
            selectedLabel={selectedLabel}
            suggestions={row.suggestions || []}
            catalogOptions={catalogOptions}
            onPick={onPick}
          />
          {/* Will-save-as line: shows the exact (thickness, size) the stock entry will use.
              Editable inline — defaults to Tally-parsed values. Once a brand+series is picked,
              the inputs gain a <datalist> with the thicknesses/sizes that family actually carries
              (sourced from the product's catalog entry), so the user gets autocomplete + can
              still type free text if Tally has a size the catalog doesn't yet track. */}
          <div className="flex items-center gap-1.5 text-[11px] text-[#5C6150] pl-1 flex-wrap">
            <span className="text-[#8F9485]">Save as →</span>
            <input
              type="text"
              list={selected ? `tally-th-${row._idx}` : undefined}
              value={row.thickness_parsed || ''}
              onChange={(e) => onThicknessChange(e.target.value)}
              placeholder="thickness"
              className={`w-20 border rounded px-1 py-0.5 font-medium focus:outline-none ${
                selected && row.thickness_parsed && selected.available_thicknesses?.length && !selected.available_thicknesses.includes(row.thickness_parsed)
                  ? 'border-amber-400 text-amber-700 bg-amber-50'
                  : 'border-[#E2E6D9] text-[#1A1C16] focus:border-[#556B2F]'
              }`}
              data-testid={`tally-row-thickness-${row._idx}`}
            />
            {selected?.available_thicknesses?.length > 0 && (
              <datalist id={`tally-th-${row._idx}`}>
                {selected.available_thicknesses.map((t) => <option key={t} value={t} />)}
              </datalist>
            )}
            <span className="text-[#8F9485]">·</span>
            <input
              type="text"
              list={selected ? `tally-sz-${row._idx}` : undefined}
              value={row.size_parsed || ''}
              onChange={(e) => onSizeChange(e.target.value)}
              placeholder="size"
              className={`w-20 border rounded px-1 py-0.5 font-medium focus:outline-none ${
                selected && row.size_parsed && selected.available_sizes?.length && !selected.available_sizes.includes(row.size_parsed)
                  ? 'border-amber-400 text-amber-700 bg-amber-50'
                  : 'border-[#E2E6D9] text-[#1A1C16] focus:border-[#556B2F]'
              }`}
              data-testid={`tally-row-size-${row._idx}`}
            />
            {selected?.available_sizes?.length > 0 && (
              <datalist id={`tally-sz-${row._idx}`}>
                {selected.available_sizes.map((s) => <option key={s} value={s} />)}
              </datalist>
            )}
            {row.quantity != null && (
              <span className="text-[#5C6150]">· {row.quantity} {row.quantity === 1 ? 'sheet' : 'sheets'}</span>
            )}
          </div>
          {selected && (selected.available_thicknesses?.length > 0 || selected.available_sizes?.length > 0) && (
            <div className="text-[10px] text-[#8F9485] pl-1 leading-tight">
              {selected.available_thicknesses?.length > 0 && (
                <div>Available thicknesses: {selected.available_thicknesses.join(', ')}</div>
              )}
              {selected.available_sizes?.length > 0 && (
                <div>Available sizes: {selected.available_sizes.join(', ')}</div>
              )}
            </div>
          )}
        </div>
      </td>
      <td className="px-2 py-2 text-center align-top">
        <Badge className={`text-[10px] ${tierStyles[row.confidence] || tierStyles.none}`}>
          {tierLabel[row.confidence] || row.confidence}
        </Badge>
      </td>
    </tr>
  );
}

/**
 * MatchCombobox — searchable picker per row.
 *
 * The list is split into two groups:
 *   1. "Top matches" — fuzzy suggestions for this Tally row (with score %).
 *   2. "All catalog products" — every stock-tracked product, alphabetised.
 *
 * cmdk's built-in `value`-based filtering handles the search; we lowercase
 * everything into the item's `value` so the user can type any fragment of
 * brand / subcategory / thickness / size and hit the right product.
 */
function MatchCombobox({ rowIdx, selectedId, selectedLabel, suggestions, catalogOptions, onPick }) {
  const [open, setOpen] = useState(false);
  // Hide suggestions from the "All products" group to avoid duplicate rows
  // when both top-match and full-catalog contain the same product_id.
  const suggestionIds = useMemo(() => new Set(suggestions.map((s) => s.product_id)), [suggestions]);
  const restCatalog = useMemo(
    () => catalogOptions.filter((o) => !suggestionIds.has(o.product_id)),
    [catalogOptions, suggestionIds]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center justify-between gap-2 border border-[#E2E6D9] rounded px-2 py-1.5 text-xs text-left bg-white hover:border-[#556B2F] focus:border-[#556B2F] focus:outline-none"
          data-testid={`tally-row-pick-${rowIdx}`}
        >
          <span className={`truncate ${selectedLabel ? 'text-[#1A1C16]' : 'text-[#8F9485] italic'}`}>
            {selectedLabel || 'Click to pick / search a product...'}
          </span>
          <ChevronsUpDown className="w-3 h-3 text-[#8F9485] flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[480px]" align="start">
        <Command>
          <CommandInput placeholder="Search by brand / subcategory / thickness / size..." className="text-xs" />
          <CommandList>
            <CommandEmpty>No products match your search.</CommandEmpty>
            {selectedId && (
              <CommandGroup heading="Selected">
                <CommandItem
                  value="__clear__ skip this row remove"
                  onSelect={() => { onPick(''); setOpen(false); }}
                  data-testid={`tally-row-clear-${rowIdx}`}
                >
                  <XCircle className="w-3 h-3 mr-2 text-red-500" /> Skip this row (clear selection)
                </CommandItem>
              </CommandGroup>
            )}
            {suggestions.length > 0 && (
              <CommandGroup heading="Top suggestions">
                {suggestions.map((s) => (
                  <CommandItem
                    key={`sug-${s.product_id}`}
                    value={`top ${s.label}`}
                    onSelect={() => { onPick(s.product_id); setOpen(false); }}
                  >
                    <Search className="w-3 h-3 mr-2 text-[#556B2F]" />
                    <span className="flex-1 truncate">{s.label}</span>
                    <span className="ml-2 text-[10px] text-[#8F9485]">{s.score}%</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {restCatalog.length > 0 && (
              <CommandGroup heading={`All catalog products (${restCatalog.length})`}>
                {restCatalog.map((o) => (
                  <CommandItem
                    key={`cat-${o.product_id}`}
                    value={o.label}
                    onSelect={() => { onPick(o.product_id); setOpen(false); }}
                  >
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
